import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { FileText, BookOpen, Shield, LayoutGrid, Sun, MoonStar, Monitor, ChevronDown, LogOut } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/primitives";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function AppShell() {
  const { mode, resolved, setMode } = useTheme();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    retry: false,
  });

  const signOut = useMutation({
    mutationFn: api.logout,
    onSettled: () => {
      // Drop every cached query so a re-login fetches fresh state.
      qc.clear();
      navigate({ to: "/login" });
    },
  });

  const nav = [
    { to: "/", label: "Applications", Icon: FileText, match: (p: string) => p === "/" || p.startsWith("/apps") },
    { to: "/queue", label: "Review Queue", Icon: LayoutGrid, match: (p: string) => p.startsWith("/queue") },
    { to: "/manual", label: "Credit Policy Manual", Icon: BookOpen, match: (p: string) => p.startsWith("/manual") },
    { to: "/admin", label: "Admin", Icon: Shield, match: (p: string) => p.startsWith("/admin") },
  ];

  return (
    <div className="flex h-screen w-full bg-background text-foreground">
      {/* left sidebar */}
      <aside className="flex h-full w-[260px] shrink-0 flex-col border-r border-border bg-card">
        <div className="flex h-16 items-center gap-3 px-6">
          <img
            src={resolved === "dark" ? "/uniphore-logo-gradient-white.png" : "/uniphore-logo-gradient-black.png"}
            alt="Uniphore"
            className="h-7 w-auto"
          />
          <span className="text-[0.7rem] font-medium uppercase tracking-[0.08em] text-muted-foreground">Credit Approval</span>
        </div>
        <nav className="flex-1 px-3 py-4">
          <ul className="space-y-1">
            {nav.map((n) => {
              const active = n.match(pathname);
              return (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                      active ? "bg-brand/10 text-brand" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <n.Icon className="h-4 w-4" strokeWidth={1.8} />
                    {n.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="border-t border-border p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="group flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label="Account menu"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand/10 text-brand text-xs font-semibold">
                  {user?.username?.slice(0, 2).toUpperCase() ?? "—"}
                </div>
                <div className="flex-1 text-sm">
                  <div className="font-medium leading-tight">{user?.username ?? "…"}</div>
                  <div className="text-xs text-muted-foreground leading-tight">{user?.role ?? "—"}</div>
                </div>
                <ChevronDown
                  className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180"
                  strokeWidth={1.8}
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-[236px]">
              <DropdownMenuLabel>
                <div className="font-semibold text-foreground">{user?.username ?? "—"}</div>
                <div className="text-[0.7rem] font-normal">{user?.role ?? ""}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => signOut.mutate()} disabled={signOut.isPending}>
                <LogOut className="h-4 w-4 text-muted-foreground" strokeWidth={1.9} />
                {signOut.isPending ? "Signing out…" : "Sign out"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* main column */}
      <div className="flex h-full flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center justify-between border-b border-border bg-background px-8">
          <Breadcrumbs />
          <div className="flex items-center gap-1 rounded-md border border-border bg-card p-0.5">
            <Button
              variant={mode === "light" ? "secondary" : "ghost"}
              size="icon"
              aria-label="Light"
              onClick={() => setMode("light")}
              className="h-8 w-8"
            >
              <Sun className="h-4 w-4" strokeWidth={1.8} />
            </Button>
            <Button
              variant={mode === "system" ? "secondary" : "ghost"}
              size="icon"
              aria-label="System"
              onClick={() => setMode("system")}
              className="h-8 w-8"
            >
              <Monitor className="h-4 w-4" strokeWidth={1.8} />
            </Button>
            <Button
              variant={mode === "dark" ? "secondary" : "ghost"}
              size="icon"
              aria-label="Dark"
              onClick={() => setMode("dark")}
              className="h-8 w-8"
            >
              <MoonStar className="h-4 w-4" strokeWidth={1.8} />
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          <div
            className={
              pathname.startsWith("/manual")
                ? "w-full px-6 py-8"
                : "mx-auto w-full max-w-6xl px-8 py-8"
            }
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function Breadcrumbs() {
  const routerState = useRouterState();
  const path = routerState.location.pathname;
  const parts: { label: string; to?: string }[] = [];
  if (path === "/") parts.push({ label: "Applications" });
  else if (path.startsWith("/apps/")) {
    parts.push({ label: "Applications", to: "/" });
    parts.push({ label: "Application" });
    const seg = path.split("/");
    if (seg[3]) parts.push({ label: seg[3].charAt(0).toUpperCase() + seg[3].slice(1) });
  } else if (path.startsWith("/queue")) parts.push({ label: "Review Queue" });
  else if (path.startsWith("/manual")) parts.push({ label: "Credit Policy Manual" });
  else if (path.startsWith("/admin")) parts.push({ label: "Admin" });

  return (
    <nav className="flex items-center text-sm text-muted-foreground">
      {parts.map((p, i) => (
        <span key={i} className="flex items-center">
          {p.to ? (
            <Link to={p.to} className="hover:text-foreground transition-colors">
              {p.label}
            </Link>
          ) : (
            <span className={i === parts.length - 1 ? "text-foreground font-medium" : ""}>{p.label}</span>
          )}
          {i < parts.length - 1 && <span className="mx-2">/</span>}
        </span>
      ))}
    </nav>
  );
}
